import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import MiniMap from './MiniMap';
import LocationFallback from './LocationFallback';

interface DisplayInfoData {
  id: number;
  name: string;
  latitude?: number | null;
  longitude?: number | null;
  city?: string | null;
  state?: string | null;
  location?: string | null;
  has_real_coordinates?: boolean;
}

interface DisplayInfoProps {
  displayInfo: DisplayInfoData;
  className?: string;
}

const DisplayInfo: React.FC<DisplayInfoProps> = ({ displayInfo, className }) => {
  if (!displayInfo) return null;

  // Determine if we have real coordinates (not 0,0 and not null)
  const hasRealCoordinates = displayInfo.has_real_coordinates || (
    displayInfo.latitude != null && 
    displayInfo.longitude != null && 
    displayInfo.latitude !== 0 && 
    displayInfo.longitude !== 0
  );

  // Format location text
  const locationText = displayInfo.city && displayInfo.state 
    ? `${displayInfo.city}, ${displayInfo.state}`
    : displayInfo.location || null;

  return (
    <div className={cn(
      "display-info mt-3 p-3 max-sm:p-2 bg-muted/30 rounded-md border border-border/30",
      className
    )}>
      {/* Display Name Link */}
      <Link
        to={`/display/${displayInfo.id}`}
        className="display-link flex items-center gap-2 text-primary hover:text-primary/80 transition-colors font-medium text-sm max-sm:text-xs mb-2"
      >
        <MapPin className="h-4 w-4 max-sm:h-3 max-sm:w-3" />
        <span className="truncate">{displayInfo.name}</span>
      </Link>
      
      {/* Location Text */}
      {locationText && (
        <p className="text-xs max-sm:text-[11px] text-muted-foreground mb-2 truncate">
          {locationText}
        </p>
      )}
      
      {/* Map or Fallback */}
      {hasRealCoordinates && displayInfo.latitude && displayInfo.longitude ? (
        <MiniMap 
          latitude={displayInfo.latitude}
          longitude={displayInfo.longitude}
          displayName={displayInfo.name}
          displayId={displayInfo.id}
          hasRealCoordinates={true}
          size="sm"
        />
      ) : (
        <LocationFallback 
          displayName={displayInfo.name}
          locationText={locationText}
        />
      )}
    </div>
  );
};

export default DisplayInfo;